import type { SpaceTimeClient } from '../../spacetime/SpaceTimeClient';
import type { NpcRuntimeData, ChestInventoryRow } from '../../model/gamestate/types';
import type { InventoryModule } from '../../spacetime/modules/InventoryModule';
import { ItemGrid } from '../itemGrid/ItemGrid';
import { GridDragDrop } from '../itemGrid/GridDragDrop';
import type { ItemSlot } from '../itemGrid/types';
import { createWindowFrame, windowId } from '../windowing';

const CHEST_GRID_ID = 'chest';
const PLAYER_GRID_ID = 'player';
const DEFAULT_PLAYER_CAPACITY = 10;

type Connection = Awaited<ReturnType<SpaceTimeClient['ensureConnection']>>;

class ChestWindowInstance {
  private readonly panel: HTMLDivElement;
  private readonly body: HTMLDivElement;
  private readonly closeBtn: HTMLButtonElement;
  private readonly frame: ReturnType<typeof createWindowFrame>;
  private chestSub: { unsubscribe: () => void } | null = null;
  private cleanupFns: Array<() => void> = [];
  private contextMenuEl: HTMLDivElement | null = null;
  private contextMenuCloseHandler: ((event: MouseEvent) => void) | null = null;
  private isDestroyed = false;

  constructor(
    private readonly client: SpaceTimeClient,
    private readonly inventory: InventoryModule,
    public readonly npcId: string,
    onDestroy: (instance: ChestWindowInstance) => void,
    windowInstanceId: number,
  ) {
    this.panel = document.createElement('div');
    this.panel.className = 'window-container';
    this.panel.style.position = 'fixed';
    this.panel.style.maxWidth = '90vw';
    this.panel.style.maxHeight = '90vh';

    const header = document.createElement('div');
    header.className = 'window-header';

    const title = document.createElement('div');
    title.className = 'window-header-title';
    title.textContent = 'Chest';
    title.style.color = '#c8a951';

    const resizeHandle = document.createElement('div');
    resizeHandle.className = 'window-resize-handle';
    resizeHandle.innerHTML = '<svg width="10" height="10" viewBox="0 0 10 10" style="pointer-events: none;"><path d="M10 0 L10 10 L0 10 Z" fill="#9ca3af" /></svg>';

    this.closeBtn = document.createElement('button');
    this.closeBtn.type = 'button';
    this.closeBtn.className = 'inventory-close';
    this.closeBtn.textContent = '×';

    header.appendChild(title);
    header.appendChild(resizeHandle);
    header.appendChild(this.closeBtn);

    this.body = document.createElement('div');
    this.body.className = 'window-content';
    Object.assign(this.body.style, {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '12px',
      overflow: 'hidden',
      minHeight: '0',
    });

    this.panel.appendChild(header);
    this.panel.appendChild(this.body);
    document.body.appendChild(this.panel);

    const defaultSize = { width: 420, height: 320 };
    const defaultPosition = {
      x: Math.max(0, Math.round((window.innerWidth - defaultSize.width) / 2)),
      y: Math.max(0, Math.round((window.innerHeight - defaultSize.height) / 2)),
    };
    this.frame = createWindowFrame({
      windowId: windowId('chest-ui', 'instance', windowInstanceId),
      container: this.panel,
      header,
      resizeHandle,
      closeButton: this.closeBtn,
      minWidth: 420,
      minHeight: 220,
      initialPosition: defaultPosition,
      initialSize: defaultSize,
      persistState: false,
    });
    this.frame.setWindowState('open');

    const close = (): void => this.destroy();
    const trapWheel = (event: WheelEvent): void => {
      event.stopPropagation();
    };
    this.closeBtn.addEventListener('click', close);
    this.panel.addEventListener('wheel', trapWheel, { passive: false });
    this.cleanupFns.push(() => this.closeBtn.removeEventListener('click', close));
    this.cleanupFns.push(() => this.panel.removeEventListener('wheel', trapWheel));
    this.cleanupFns.push(() => onDestroy(this));
  }

  public async initialize(): Promise<void> {
    const conn = await this.client.ensureConnection();
    if (this.isDestroyed) {
      return;
    }

    const chestSub = await this.inventory.subscribeToChestInventory((rows) => {
      if (this.isDestroyed) {
        return;
      }
      if (rows.length === 0) {
        const npcStillExists = [...conn.db.currentMapRuntimeNpcs.iter()]
          .some((n: { npcId: string }) => n.npcId === this.npcId);
        if (!npcStillExists) {
          this.destroy();
          return;
        }
      }
      const currentRows = rows.filter((row) => row.npcId === this.npcId);
      this.renderGrids(currentRows, conn);
    });
    if (this.isDestroyed) {
      chestSub.unsubscribe();
      return;
    }
    this.chestSub = chestSub;

    const onNpcDelete = (_ctx: unknown, row: { npcId: string }) => {
      if (row.npcId === this.npcId) {
        this.destroy();
      }
    };
    conn.db.currentMapRuntimeNpcs.onDelete(onNpcDelete);
    this.cleanupFns.push(() => conn.db.currentMapRuntimeNpcs.removeOnDelete(onNpcDelete));

    const onInvChange = () => {
      if (this.isDestroyed) {
        return;
      }
      const chestRows = [...conn.db.accessibleChestInventory.iter()]
        .map((r) => r as unknown as ChestInventoryRow)
        .filter((row) => row.npcId === this.npcId);
      this.renderGrids(chestRows, conn);
    };
    conn.db.myInventory.onInsert(onInvChange);
    conn.db.myInventory.onDelete(onInvChange);
    conn.db.myInventory.onUpdate(onInvChange);
    this.cleanupFns.push(() => {
      conn.db.myInventory.removeOnInsert(onInvChange);
      conn.db.myInventory.removeOnDelete(onInvChange);
      conn.db.myInventory.removeOnUpdate(onInvChange);
    });

    const initialRows = [...conn.db.accessibleChestInventory.iter()]
      .map((r) => r as unknown as ChestInventoryRow)
      .filter((row) => row.npcId === this.npcId);
    this.renderGrids(initialRows, conn);
  }

  public destroy(): void {
    if (this.isDestroyed) {
      return;
    }
    this.isDestroyed = true;
    this.hideContextMenu();
    this.chestSub?.unsubscribe();
    this.chestSub = null;
    for (const fn of this.cleanupFns.splice(0)) {
      fn();
    }
    this.body.innerHTML = '';
    this.frame.destroy();
    this.panel.remove();
  }

  private renderGrids(chestRows: ChestInventoryRow[], conn: Connection): void {
    this.body.innerHTML = '';

    const chestCapacity = Math.max(chestRows.length, 6);
    const chestSlots: ItemSlot[] = chestRows
      .sort((a, b) => a.slotIndex - b.slotIndex)
      .map((r) => {
        const meta = this.parseMeta(r.metadataJson);
        const emoji = (meta.emoji as string | undefined) ?? '📦';
        const displayName = (meta.display_name as string | undefined) ?? r.itemId;
        return {
          id: String(r.slotIndex),
          item: { emoji, displayName, quantity: r.quantity },
        };
      });

    const playerSlots: ItemSlot[] = [...conn.db.myInventory.iter()]
      .sort((a, b) => a.slotIndex - b.slotIndex)
      .map((r) => {
        const meta = this.parseMeta(r.metadataJson);
        const emoji = (meta.emoji as string | undefined) ?? '📦';
        const displayName = (meta.display_name as string | undefined) ?? r.itemId;
        return {
          id: String(r.slotIndex),
          dragDataId: r.inventoryId,
          item: { emoji, displayName, quantity: Number(r.quantity) },
        };
      });
    const playerCapacity = this.resolvePlayerCapacity(conn, playerSlots);

    const chestGrid = new ItemGrid({
      gridId: CHEST_GRID_ID,
      label: 'Chest',
      capacity: chestCapacity,
      slots: chestSlots,
      labelColor: '#c8a951',
      scrollable: true,
    });

    const playerGrid = new ItemGrid({
      gridId: PLAYER_GRID_ID,
      label: 'Your Inventory',
      capacity: playerCapacity,
      slots: playerSlots,
      scrollable: true,
    });

    chestGrid.onSlotContextMenu = (slotId, event) => {
      const row = chestRows.find((entry) => entry.slotIndex === Number(slotId));
      if (!row || row.quantity <= 1) return;
      this.showSplitContextMenu(
        event.clientX,
        event.clientY,
        (quantity) => this.inventory.splitChestItem(this.npcId, row.slotIndex, quantity),
        row.quantity,
      );
    };

    playerGrid.onSlotContextMenu = (slotId, event) => {
      const row = [...conn.db.myInventory.iter()].find((entry) => entry.slotIndex === Number(slotId));
      if (!row || Number(row.quantity) <= 1) return;
      this.showSplitContextMenu(
        event.clientX,
        event.clientY,
        (quantity) => this.inventory.splitInventoryItem(row.inventoryId, quantity),
        Number(row.quantity),
      );
    };

    chestGrid.onSlotClick = (slotId) => {
      const firstOpenSlot = this.findFirstOpenPlayerSlot(playerSlots, playerCapacity);
      if (firstOpenSlot < 0) {
        return;
      }
      void this.inventory.takeItemFromChestToSlot(this.npcId, Number(slotId), firstOpenSlot);
    };

    const dnd = new GridDragDrop([
      {
        from: CHEST_GRID_ID,
        to: PLAYER_GRID_ID,
        onTransfer: (sourceSlotId, targetSlotId) => {
          void this.inventory.takeItemFromChestToSlot(
            this.npcId,
            Number(sourceSlotId),
            Number(targetSlotId),
          );
        },
      },
      {
        from: CHEST_GRID_ID,
        to: CHEST_GRID_ID,
        onTransfer: (sourceSlotId, targetSlotId) => {
          void this.inventory.moveChestItem(
            this.npcId,
            Number(sourceSlotId),
            Number(targetSlotId),
          );
        },
      },
      {
        from: PLAYER_GRID_ID,
        to: PLAYER_GRID_ID,
        onTransfer: (sourceSlotId, targetSlotId) => {
          const sourceRow = [...conn.db.myInventory.iter()].find((r) => r.slotIndex === Number(sourceSlotId));
          if (!sourceRow) return;
          void this.inventory.moveInventoryItem(sourceRow.inventoryId, Number(targetSlotId));
        },
      },
    ]);
    dnd.register(chestGrid);
    dnd.register(playerGrid);

    this.body.appendChild(chestGrid.element);
    this.body.appendChild(playerGrid.element);
  }

  private resolvePlayerCapacity(conn: Connection, playerSlots: ItemSlot[]): number {
    const myPlayerRow = [...conn.db.myPlayer.iter()][0] as { inventoryCapacity?: number } | undefined;
    const fromView = Number(myPlayerRow?.inventoryCapacity ?? 0);
    if (Number.isFinite(fromView) && fromView > 0) {
      return fromView;
    }
    return Math.max(DEFAULT_PLAYER_CAPACITY, playerSlots.length);
  }

  private findFirstOpenPlayerSlot(playerSlots: ItemSlot[], capacity: number): number {
    const occupied = new Set<number>(
      playerSlots
        .map((slot) => Number(slot.id))
        .filter((idx) => Number.isFinite(idx) && idx >= 0),
    );
    for (let i = 0; i < capacity; i += 1) {
      if (!occupied.has(i)) {
        return i;
      }
    }
    return -1;
  }

  private parseMeta(json: string): Record<string, unknown> {
    try {
      return JSON.parse(json) as Record<string, unknown>;
    } catch {
      return {};
    }
  }

  private showSplitContextMenu(
    x: number,
    y: number,
    action: (quantity: number) => Promise<void>,
    sourceQuantity: number,
  ): void {
    this.hideContextMenu();
    const menu = document.createElement('div');
    Object.assign(menu.style, {
      position: 'fixed',
      left: `${x}px`,
      top: `${y}px`,
      background: '#0f172a',
      border: '1px solid #334155',
      borderRadius: '6px',
      padding: '4px',
      zIndex: '100000',
      minWidth: '140px',
      boxShadow: '0 8px 18px rgba(0,0,0,0.45)',
    });

    const splitBtn = document.createElement('button');
    splitBtn.type = 'button';
    splitBtn.textContent = 'Split item';
    Object.assign(splitBtn.style, {
      width: '100%',
      textAlign: 'left',
      background: 'transparent',
      border: 'none',
      color: '#e2e8f0',
      padding: '6px 8px',
      cursor: 'pointer',
      borderRadius: '4px',
    });
    splitBtn.addEventListener('mouseenter', () => {
      splitBtn.style.background = 'rgba(148, 163, 184, 0.16)';
    });
    splitBtn.addEventListener('mouseleave', () => {
      splitBtn.style.background = 'transparent';
    });
    splitBtn.addEventListener('click', () => {
      this.hideContextMenu();
      const max = sourceQuantity - 1;
      const raw = window.prompt(`Split amount (1-${max})`, '1');
      if (raw === null) return;
      const quantity = Number(raw);
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > max) return;
      void action(quantity).catch((err) => console.error('[ChestUI] Failed to split stack', err));
    });
    menu.appendChild(splitBtn);

    document.body.appendChild(menu);
    this.contextMenuEl = menu;
    this.contextMenuCloseHandler = (event: MouseEvent) => {
      if (!menu.contains(event.target as Node)) {
        this.hideContextMenu();
      }
    };
    window.setTimeout(() => {
      if (this.contextMenuCloseHandler) {
        window.addEventListener('mousedown', this.contextMenuCloseHandler);
      }
    }, 0);
  }

  private hideContextMenu(): void {
    if (this.contextMenuCloseHandler) {
      window.removeEventListener('mousedown', this.contextMenuCloseHandler);
      this.contextMenuCloseHandler = null;
    }
    if (this.contextMenuEl) {
      this.contextMenuEl.remove();
      this.contextMenuEl = null;
    }
  }
}

export class ChestUI {
  private static nextWindowInstanceId = 1;
  private activeWindow: ChestWindowInstance | null = null;

  constructor(
    private readonly client: SpaceTimeClient,
    private readonly inventory: InventoryModule,
  ) {}

  public getCurrentNpcId(): string | null {
    return this.activeWindow?.npcId ?? null;
  }

  public async show(npc: NpcRuntimeData): Promise<void> {
    if (this.activeWindow?.npcId === npc.npcId) {
      return;
    }

    this.activeWindow?.destroy();
    const instance = new ChestWindowInstance(
      this.client,
      this.inventory,
      npc.npcId,
      (windowInstance) => {
        if (this.activeWindow === windowInstance) {
          this.activeWindow = null;
        }
      },
      ChestUI.nextWindowInstanceId++,
    );
    this.activeWindow = instance;
    await instance.initialize();
  }

  public hide(): void {
    this.activeWindow?.destroy();
    this.activeWindow = null;
  }

  public destroy(): void {
    this.hide();
  }
}
